import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'
import { useRef, useEffect } from 'react'
import { useHookstate } from '@hookstate/core'
import Styled from 'styled-components'

const CollapsibleList = Styled.ul<{ hasScroll: boolean; styles: React.CSSProperties }>`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  margin: 0;
  ${({ styles }) => styles && { ...styles }}
  padding-right: ${({ hasScroll, styles }) => (hasScroll ? '0.8rem' : styles.paddingRight)};
`
const CollapsibleCard = Styled.li`
  width: 100%;
  height: auto;
  margin-bottom: 0.8rem;
`
const CollapsibleHeader = Styled.header<{
  show: boolean
  styles: React.CSSProperties
}>`
  width: 100%;
  height: 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 0;
  border-radius: 0;
  padding: 0;
  background: #ffffff;

  ${({ styles }) => styles && { ...styles }}
`
const CollapsibleBody = Styled.section<{ show: boolean; styles: React.CSSProperties }>`
  width: 100%;
  height: ${({ show }) => (show ? 'auto' : '0')};
  display: flex;
  justify-content: flex-start;
  border-left: ${({ show }) => (show ? '1px solid #dde0e5' : 'none')};
  border-right: ${({ show }) => (show ? '1px solid #dde0e5' : 'none')};
  border-bottom: ${({ show }) => (show ? '1px solid #dde0e5' : 'none')};
  transition: height 0.5s linear, border-bottom 0.5s linear, border-left 0.5s linear, border-right 0.5s linear;
  @media (max-width:768px){
    flex-direction: column;
  }
  ${({ styles }) => styles && { ...styles }}
`
const ArrowIcon = Styled.div<{ color?: string; size: 'large' | 'small'; cursor?: string }>`
  width: 2rem;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  & > span {
    color: ${({ color }) => (color ? color : 'rgba(0, 0, 0, 0.85)')};
    & > svg{
      cursor: ${({ cursor }) => (cursor ? cursor : 'pointer')};
      width: ${({ size }) => (size === 'large' ? '2rem' : '1.4rem')};
      height: ${({ size }) => (size === 'large' ? '2rem' : '1.5rem')};
    }
  }
`
const HeadContent = Styled.div`
  width: calc(100% - 2rem);
  height: 100%;
`

type CollapsibleCardListProps = {
  dataArray: any[]
  headerContent: (item: any) => React.ReactNode
  bodyContent: () => React.ReactNode
  listStyle?: React.CSSProperties
  headerStyle?: React.CSSProperties
  bodyStyle?: React.CSSProperties
  arrowSize?: 'large' | 'small'
  arrowColorCB?: (item: any) => string
  arrowCursorCB?: (item: any) => string
  selectionCallback?: (item: any) => void
  disabledCallback?: (item: any) => boolean
}

const CollapsibleCardList = ({
  dataArray,
  headerContent,
  bodyContent,
  listStyle,
  headerStyle,
  bodyStyle,
  arrowSize,
  arrowColorCB,
  arrowCursorCB,
  selectionCallback,
  disabledCallback
}: CollapsibleCardListProps) => {
  const listRef = useRef(null)
  const initialState = {}
  Object.entries(dataArray).forEach((item, index) => {
    initialState[index] = false
  })
  const displayValues = useHookstate(initialState)
  const hasScroll = useHookstate<boolean>(false)

  useEffect(() => {
    const container = listRef.current
    const handleScroll = () => {
      hasScroll.set(container.scrollHeight > container.clientHeight)
    }
    container.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <CollapsibleList ref={listRef} styles={listStyle} hasScroll={hasScroll.get()}>
      {dataArray.map((item, index) => {
        return (
          <CollapsibleCard key={index} aria-label='collapsible-container'>
            <CollapsibleHeader show={displayValues.get()[index]} styles={headerStyle}>
              <HeadContent>{headerContent(item)}</HeadContent>
              <ArrowIcon
                size={arrowSize}
                color={arrowColorCB && arrowColorCB(item)}
                cursor={arrowCursorCB && arrowCursorCB(item)}
              >
                {displayValues.get()[index] ? (
                  <CaretUpIcon
                    cursor={'pointer'}
                    onClick={() => {
                      if (disabledCallback && disabledCallback(item)) return
                      displayValues.set(initialState)
                      selectionCallback && selectionCallback({})
                    }}
                  />
                ) : (
                  <CaretDownIcon
                    cursor={'pointer'}
                    onClick={() => {
                      if (disabledCallback && disabledCallback(item)) return
                      displayValues.set({ ...initialState, [index]: true })
                      selectionCallback && selectionCallback(item)
                    }}
                  />
                )}
              </ArrowIcon>
            </CollapsibleHeader>
            <CollapsibleBody show={displayValues.get()[index]} styles={bodyStyle}>
              {displayValues.get()[index] && bodyContent()}
            </CollapsibleBody>
          </CollapsibleCard>
        )
      })}
    </CollapsibleList>
  )
}

export { CollapsibleCardList }
